import { useNavigate } from 'react-router';
import { motion } from 'framer-motion';
import {
  Layers, Filter, Beaker, Volume2, Wind, Trash2, Box,
  FlaskConical, ArrowRight, Droplets, CheckCircle2, AlertCircle,
  Info, CircleDot, Gauge, Zap, ChevronRight, Waves
} from 'lucide-react';
import { useSession } from '@/context/SessionContext';
import { useMemo } from 'react';
import type { ProcessTrainRecommendation } from '@/lib/reportAnalysis';
import PageHeader from '@/components/PageHeader';

/* ─── Equipment List Overview ─────────────────────────
   Dashboard listing all process equipment with summary
   cards, status indicators, and quick navigation.
   When an active customer has a remembered assessment,
   the process train highlights recommended equipment.
   ──────────────────────────────────────────────────── */

interface EquipmentItem {
  id: string;
  name: string;
  description: string;
  icon: React.ElementType;
  color: string;
  path: string;
  specs: { label: string; value: string }[];
  alerts?: string[];
}

const EQUIPMENT: EquipmentItem[] = [
  {
    id: 'eq', name: 'Equalization Tank', description: 'Flow balancing, peak shaving & mixing',
    icon: Volume2, color: '#38bdf8', path: '/eq-spec',
    specs: [{ label: 'Detention', value: '8–24 h' }, { label: 'Working Volume', value: '30–80%' }, { label: 'Mixer Power', value: '1–10 kW' }],
    alerts: ['Short-circuiting risk if L:W > 5:1'],
  },
  {
    id: 'ph', name: 'pH Adjustment', description: 'Neutralization with reagent dosing',
    icon: Beaker, color: '#10b981', path: '/ph-spec',
    specs: [{ label: 'Retention', value: '15–60 min' }, { label: 'Reagents', value: 'NaOH / HCl / CO2' }, { label: 'Mixer G', value: '50–200 s-1' }],
    alerts: ['Monitor alkalinity shortfall'],
  },
  {
    id: 'daf', name: 'DAF Unit', description: 'Dissolved air flotation for solids & FOG',
    icon: Waves, color: '#06b6d4', path: '/daf-spec',
    specs: [{ label: 'Pressure', value: '4–6 bar' }, { label: 'Recycle Rate', value: '10–30%' }, { label: 'SLR', value: '2–8 m³/m².h' }],
    alerts: ['FOG alert at >50 mg/L', 'Blanket thickness monitor'],
  },
  {
    id: 'lamella', name: 'Lamella Clarifier', description: 'Inclined plate settling for TSS removal',
    icon: Layers, color: '#8b5cf6', path: '/lamella-spec',
    specs: [{ label: 'Plate Angle', value: '50–60°' }, { label: 'SOR', value: '0.5–1.5 m³/m².h' }, { label: 'Plate Spacing', value: '50–80 mm' }],
    alerts: ['Sludge hopper fill rate monitor'],
  },
  {
    id: 'aeration', name: 'Aeration / MBBR', description: 'Biological treatment with F/M control',
    icon: Wind, color: '#10b981', path: '/aeration-spec',
    specs: [{ label: 'F/M Ratio', value: '0.1–0.4' }, { label: 'SRT', value: '10–25 days' }, { label: 'DO Target', value: '2–3 mg/L' }],
    alerts: ['Nitrification risk at low DO'],
  },
  {
    id: 'secondary', name: 'Secondary Clarifier', description: 'Settling tank for biomass separation',
    icon: CircleDot, color: '#06b6d4', path: '/secondary-spec',
    specs: [{ label: 'SOR', value: '0.5–1.2 m³/m².h' }, { label: 'SLR', value: '3–6 kg/m².h' }, { label: 'SVI', value: '80–150 mL/g' }],
    alerts: ['Bulking risk at SVI >200', 'Solids flux overload'],
  },
  {
    id: 'buffer', name: 'Buffer Tank', description: 'Surge protection & pump control',
    icon: Box, color: '#f59e0b', path: '/buffer-spec',
    specs: [{ label: 'Detention', value: '0.5–4 h' }, { label: 'Working Volume', value: '50–85%' }, { label: 'Pump Starts', value: '<30/day' }],
    alerts: ['Overflow at high setpoint >90%'],
  },
  {
    id: 'multimedia', name: 'Multimedia Filter', description: 'Three-layer depth filtration',
    icon: Layers, color: '#d97706', path: '/multimedia-spec',
    specs: [{ label: 'Filtration Rate', value: '8–15 m³/m².h' }, { label: 'Head Loss', value: '<3 m clean' }, { label: 'Backwash', value: '15–25 m³/m².h' }],
    alerts: ['Media migration at excessive rate'],
  },
  {
    id: 'gac', name: 'GAC Filter', description: 'Activated carbon adsorption & polishing',
    icon: Filter, color: '#8b5cf6', path: '/gac-spec',
    specs: [{ label: 'EBCT', value: '10–20 min' }, { label: 'Iodine No.', value: '>850 mg/g' }, { label: 'SLR', value: '5–15 m³/m².h' }],
    alerts: ['Breakthrough monitor', 'Fouling at TSS >20'],
  },
  {
    id: 'sludge', name: 'Sludge Holding', description: 'Thickening, storage & polymer dosing',
    icon: Trash2, color: '#b45309', path: '/sludge-spec',
    specs: [{ label: 'Thickening', value: '2–4x' }, { label: 'Storage', value: '2–7 days' }, { label: 'Polymer', value: '4–8 kg/t DS' }],
    alerts: ['Anaerobic risk at >24h', 'Overfill monitor'],
  },
  {
    id: 'cascade-aerator', name: 'Cascade Aerator', description: 'Multi-step DO transfer via staged weirs',
    icon: Droplets, color: '#06b6d4', path: '/cascade-aerator-spec',
    specs: [{ label: 'Steps', value: '2–8' }, { label: 'Weir Load', value: '≤36 L/s/m' }, { label: 'Final DO', value: '≥8 mg/L' }],
    alerts: ['Weir loading >36 L/s/m', 'Low DO at high temp'],
  },
  {
    id: 'ammonia-stripper', name: 'VTA Hot Stripper', description: '3S VTA two-stage hot ammonia stripping (80–90°C) with series cascade, urea hydrolysis, flare back-pressure — RCER-2025',
    icon: Wind, color: '#10b981', path: '/ammonia-stripper-spec',
    specs: [{ label: 'pH Range', value: '10.5–11.0' }, { label: 'Temperature', value: '80–90°C' }, { label: 'Stages', value: '2 × 3S VTA' }],
    alerts: ['pH < 10.5 — inefficient stripping', 'Steam content correction required at T > 70°C', 'Check urea hydrolysis at upset load'],
  },
  {
    id: 'packed-column-stripper', name: 'Packed-Column Stripper', description: 'Conventional countercurrent packed-bed air stripping for ambient/warm service (5–40°C)',
    icon: Wind, color: '#06b6d4', path: '/packed-column-stripper',
    specs: [{ label: 'pH Required', value: '≥10.0' }, { label: 'Air:Water', value: '100–400' }, { label: 'Temp Range', value: '5–40°C' }],
    alerts: ['pH < 10 — inefficient stripping', 'Linear HTU model invalid above 40°C — use VTA tool'],
  },
  {
    id: 'desalination-daf', name: 'Desalination DAF', description: 'Dissolved air flotation for seawater RO pre-treatment',
    icon: Waves, color: '#22d3ee', path: '/desalination-daf-spec',
    specs: [{ label: 'SLR', value: '≤70 m/h' }, { label: 'Recycle', value: '15–20%' }, { label: 'Pressure', value: '5–6 bar' }],
    alerts: ['Saline hydraulics — Henry\'s Law correction', 'Salinity affects air solubility'],
  },
];

const FLOW_STAGES = [
  { id: 'eq', name: 'EQ', color: '#38bdf8' },
  { id: 'ph', name: 'pH', color: '#10b981' },
  { id: 'daf', name: 'DAF', color: '#06b6d4' },
  { id: 'lamella', name: 'Lamella', color: '#8b5cf6' },
  { id: 'aeration', name: 'Aeration', color: '#10b981' },
  { id: 'secondary', name: 'Secondary', color: '#06b6d4' },
  { id: 'buffer', name: 'Buffer', color: '#f59e0b' },
  { id: 'multimedia', name: 'MMF', color: '#d97706' },
  { id: 'gac', name: 'GAC', color: '#8b5cf6' },
  { id: 'cascade-aerator', name: 'Cascade', color: '#06b6d4' },
  { id: 'ammonia-stripper', name: 'NH₃ Strip', color: '#10b981' },
];

/* ─── Map process train recommendations to equipment IDs ─── */
function mapProcessToEquipmentIds(processTrain: ProcessTrainRecommendation[]): string[] {
  const ids = new Set<string>();
  for (const stage of processTrain) {
    const p = stage.process.toLowerCase();
    if (p.includes('daf')) ids.add('daf');
    if (p.includes('screening') || p.includes('grit')) ids.add('eq');
    if (p.includes('anaerobic') || p.includes('activated sludge') || p.includes('biological') || p.includes('nitrification') || p.includes('denitrification')) ids.add('aeration');
    if (p.includes('sedimentation') || (p.includes('clarifier') && !p.includes('secondary'))) ids.add('lamella');
    if (p.includes('ph') || p.includes('neutralisation')) ids.add('ph');
    if (p.includes('secondary')) ids.add('secondary');
    if (p.includes('filtration') && !p.includes('tertiary')) ids.add('multimedia');
    if (p.includes('tertiary') || p.includes('uv') || p.includes('chlorination')) ids.add('gac');
    if (p.includes('sludge') || p.includes('thickening')) ids.add('sludge');
    if (p.includes('buffer')) ids.add('buffer');
  }
  return Array.from(ids);
}

function loadCustomerProcessTrain(customerId: string | number): ProcessTrainRecommendation[] | null {
  try {
    const raw = localStorage.getItem(`rb_process_train_${customerId}`);
    if (raw) return JSON.parse(raw);
  } catch { /* ignore */ }
  return null;
}

function EquipmentCard({ item, index, isRecommended }: { item: EquipmentItem; index: number; isRecommended: boolean }) {
  const navigate = useNavigate();
  const Icon = item.icon;

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05, duration: 0.3 }}
      className={`rb-inner-card p-4 hover:-translate-y-0.5 hover:shadow-2xl transition-all group cursor-pointer ${
        isRecommended ? 'border-yellow-500/50 shadow-[0_4px_16px_rgba(0,0,0,0.55),0_0_22px_rgba(234,179,8,0.18)]' : ''
      }`}
      onClick={() => navigate(item.path)}
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: `${item.color}15` }}>
          <Icon className="w-5 h-5" style={{ color: item.color }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-200 group-hover:text-white transition-colors">{item.name}</h3>
            <ArrowRight className="w-4 h-4 text-slate-600 group-hover:text-slate-300 transition-colors" />
          </div>
          <p className="text-[11px] text-slate-500 mt-0.5">{item.description}</p>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 mt-3">
        {item.specs.map((spec) => (
          <div key={spec.label} className="p-2 rb-card-sm">
            <p className="text-[10px] text-slate-500">{spec.label}</p>
            <p className="text-[11px] font-bold" style={{ color: item.color }}>{spec.value}</p>
          </div>
        ))}
      </div>

      {item.alerts && item.alerts.length > 0 && (
        <div className="mt-2 space-y-1">
          {item.alerts.map((alert, i) => (
            <div key={i} className="flex items-start gap-1.5 text-[10px] text-amber-400">
              <AlertCircle className="w-3 h-3 mt-0.5 shrink-0" />
              <span>{alert}</span>
            </div>
          ))}
        </div>
      )}

      {isRecommended && (
        <div className="mt-2 flex items-center gap-1.5 text-[10px] text-yellow-400 bg-yellow-500/5 border border-yellow-500/10 rounded-lg px-2 py-1">
          <CheckCircle2 className="w-3 h-3" />
          <span>Recommended for current assessment</span>
        </div>
      )}
    </motion.div>
  );
}

/* ─── Flow Diagram ──────────────────────────────────── */
function FlowDiagram({ recommendedIds }: { recommendedIds: string[] }) {
  const navigate = useNavigate();
  const hasRecommendations = recommendedIds.length > 0;

  return (
    <div className="p-4 overflow-x-auto rb-card">
      <div className="flex items-center justify-between mb-3">
        <p className="text-[10px] text-slate-500 uppercase tracking-wider font-bold">Process Train</p>
        {hasRecommendations && (
          <span className="text-[10px] text-yellow-400 bg-yellow-500/10 border border-yellow-500/20 rounded-full px-2 py-0.5 flex items-center gap-1">
            <Zap className="w-3 h-3" /> Live Assessment
          </span>
        )}
      </div>
      <div className="flex items-center gap-2 min-w-max">
        {FLOW_STAGES.map((stage, i) => {
          const isRec = recommendedIds.includes(stage.id);
          return (
            <div key={stage.id} className="flex items-center gap-2">
              <button
                onClick={() => navigate(`/${stage.id}-spec`)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all hover:scale-105 ${
                  isRec
                    ? 'border-yellow-500/40 bg-yellow-500/10 text-yellow-400 shadow-[0_0_8px_rgba(234,179,8,0.15)]'
                    : 'border-slate-800/30 bg-slate-800/30 text-slate-500'
                }`}
              >
                {stage.name}
              </button>
              {i < FLOW_STAGES.length - 1 && (
                <ChevronRight className={`w-3.5 h-3.5 ${isRec && recommendedIds.includes(FLOW_STAGES[i + 1].id) ? 'text-yellow-500/60' : 'text-slate-400'}`} />
              )}
            </div>
          );
        })}
      </div>
      {/* Sludge return line */}
      <div className="mt-3 flex items-center gap-2">
        <div className="h-px flex-1 bg-slate-800" />
        <button
          onClick={() => navigate('/sludge-spec')}
          className={`px-3 py-1 rounded-lg text-[10px] font-medium border transition-all hover:scale-105 ${
            recommendedIds.includes('sludge')
              ? 'border-yellow-500/30 bg-yellow-500/10 text-yellow-500'
              : 'border-slate-800/30 bg-slate-800/20 text-slate-600'
          }`}
        >
          Sludge Line <ArrowRight className="w-3 h-3 inline ml-1" />
        </button>
        <div className="h-px flex-1 bg-slate-800" />
      </div>
    </div>
  );
}

/* ─── Main Page ─────────────────────────────────────── */
export default function EquipmentListPage() {
  const { currentCustomer } = useSession();

  const recommendedIds = useMemo(() => {
    if (!currentCustomer) return [];
    const train = loadCustomerProcessTrain(currentCustomer.id);
    if (!train) return [];
    return mapProcessToEquipmentIds(train);
  }, [currentCustomer]);

  return (
    <div className="space-y-5">
      <PageHeader
        icon={FlaskConical}
        iconColor="text-emerald-400"
        title="Equipment Suite"
        subtitle="Full wastewater treatment process equipment — click any card to configure"
        badge={currentCustomer ? (
          <div className="flex items-center gap-2 text-[10px] text-cyan-400 bg-cyan-500/5 border border-cyan-500/20 rounded-lg px-3 py-1.5">
            <Gauge className="w-3 h-3" />
            <span>Customer: {currentCustomer.company || currentCustomer.name}</span>
          </div>
        ) : undefined}
      />

      {/* PROCESS FLOW */}
      <FlowDiagram recommendedIds={recommendedIds} />

      {/* LEGEND */}
      <div className="flex flex-wrap items-center gap-3 text-[10px] text-slate-500">
        <div className="flex items-center gap-1.5"><CheckCircle2 className="w-3 h-3 text-emerald-400" /><span>Optimal range shown</span></div>
        <div className="flex items-center gap-1.5"><AlertCircle className="w-3 h-3 text-amber-400" /><span>Alert conditions listed</span></div>
        <div className="flex items-center gap-1.5"><Info className="w-3 h-3 text-slate-400" /><span>All specs adjustable with sliders</span></div>
        {recommendedIds.length > 0 && (
          <div className="flex items-center gap-1.5"><Zap className="w-3 h-3 text-yellow-400" /><span>Yellow = recommended from assessment</span></div>
        )}
      </div>

      {/* EQUIPMENT GRID */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {EQUIPMENT.map((item, i) => (
          <EquipmentCard key={item.id} item={item} index={i} isRecommended={recommendedIds.includes(item.id)} />
        ))}
      </div>

      {/* FOOTER NOTES */}
      <div className="p-4 space-y-2 rb-inner-card">
        <h3 className="text-xs font-semibold text-slate-200 flex items-center gap-2">
          <Info className="w-3.5 h-3.5 text-amber-400" /> Engineering Notes
        </h3>
        <div className="space-y-1.5 text-[10px] text-slate-400">
          <div className="flex items-start gap-2">
            <ChevronRight className="w-3 h-3 mt-0.5 shrink-0 text-slate-500" />
            <span>Each equipment page includes 3 preset wastewater profiles (Dairy, Brewery, Fish Processing) that can be fully customised.</span>
          </div>
          <div className="flex items-start gap-2">
            <ChevronRight className="w-3 h-3 mt-0.5 shrink-0 text-slate-500" />
            <span>All calculations use standard wastewater engineering equations: Stokes law, Ergun equation, Vesilind settling, F/M ratio, SRT, SOR, SLR, etc.</span>
          </div>
          <div className="flex items-start gap-2">
            <ChevronRight className="w-3 h-3 mt-0.5 shrink-0 text-slate-500" />
            <span>Alerts trigger automatically when parameters exceed safe operating ranges. Save configurations to localStorage for persistence.</span>
          </div>
        </div>
      </div>
    </div>
  );
}
